import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';

import { Stack } from '@mui/material';
import moment from 'moment';

import { KaseApi, KasePropertyApi } from '@/api';
import { DATE_TIME_FORMAT } from '@/config';
import { useKaseContext } from '@/hooks';
import { IKaseCfsLink, IProperty } from '@/models';
import { useKaseMainContext } from '@/pages/Kases/KaseEdit/KaseMainContext';
import { useToastStore } from '@/store';
import { formatPersonName } from '@/utils';

import { PrintLabelItem } from './PrintLabelItem';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import { propertyPrintLabelFields } from '../../data';
import { useKasePropertyContext } from '../../PropertyContext';

export const PropertyBulkPrintLabel = () => {
  const { PropertyType } = useKaseMainContext();
  const { kase } = useKaseContext();
  const { updateToast } = useToastStore();
  const [properties, setProperties] = useState<IProperty[]>();
  const { custodyModalValues } = useKasePropertyContext();
  const [kaseCfsLinks, setKaseCfsLinks] = useState<IKaseCfsLink[]>([]);

  const printingArea = useRef<HTMLDivElement | null>(null);
  const handlePrint = useReactToPrint({});

  useEffect(() => {
    const fetchLinkedKaseCfs = async () => {
      if (!kase?._id) return;

      try {
        const res = await KaseApi.getLinkedCFS(kase._id);
        setKaseCfsLinks((res?.data as any)?.results || res?.data);
      } catch (error: any) {
        updateToast({ open: true, message: error.message });
      }
    };

    const fetchKaseProperties = async () => {
      if (!kase?._id) return;
      const { bulkSelection } = custodyModalValues;
      try {
        const response = await KasePropertyApi.list(kase._id);
        if (bulkSelection) {
          // If bulkSelection is not undefined, it means user selected bulk actions
          const filteredProperties = response.data.filter((property) => {
            return property._id && bulkSelection.includes(property._id);
          });
          setProperties(filteredProperties);
        } else {
          setProperties(response.data);
        }
      } catch (error: any) {
        updateToast({ open: true, message: error.message });
      }
    };

    fetchLinkedKaseCfs();
    fetchKaseProperties();
  }, [kase?._id]);

  // Function to dynamically get property values based on key
  const getItemDynamically = (key: string, property: IProperty | undefined) => {
    const personDetails = formatPersonName(property?.createdBy);
    const dateLogged = property?.loggedInfo?.loggedDate
      ? moment(property?.loggedInfo?.loggedDate).format(DATE_TIME_FORMAT)
      : '';
    const allTypes = PropertyType.values();
    const physicalType =
      property?.type && PropertyType.size
        ? [...allTypes].find((val) => val.key === property.type)?.label //PropertyTypeList[property.type as keyof typeof PropertyTypeList]
        : undefined;

    const valueLookup: Record<string, string | undefined> = {
      propertyType: property?.typeTag || '',
      submittedBy: personDetails || '',
      date: dateLogged,
      description: property?.description || '',
      ...(physicalType && { type: physicalType }),
    };

    return valueLookup[key] || '';
  };

  return (
    <Stack sx={{ maxHeight: 1000, overflow: 'auto', paddingBottom: '40px' }}>
      <SideModalTitle titleClassName="customVariant">
        Print labels
      </SideModalTitle>

      <Stack ref={printingArea} sx={{ p: 2 }}>
        {properties?.map((property) => {
          return (
            <Stack key={property._id}>
              {propertyPrintLabelFields.map(({ key, items }) => (
                <PrintLabelItem
                  key={key}
                  keyId={key}
                  kase={kase || null}
                  items={items}
                  propertyDetails={{
                    ...property,
                    relatedCFSNumber: kaseCfsLinks?.[0]?.cfs?.number || '',
                  }}
                  getItemValue={getItemDynamically}
                />
              ))}
            </Stack>
          );
        })}
      </Stack>
      <PropertyModalFooter
        onPrint={() => handlePrint(() => printingArea.current)}
      />
    </Stack>
  );
};
